import { FC, useCallback, useContext, useEffect, useState } from 'react';

import { useLazyQuery, useQuery, useReactiveVar } from '@apollo/client';

import { AuthContext } from 'auth';
import { clientsReloadTrigger } from 'index';
import {
  CLIENTS,
  GROUPS_TITLES,
  IClientsRequest,
  IClientsResponse,
  TGroupsTitlesResponse,
} from 'schemas';
import { debounce } from 'utils';

import { VARIABLES } from './constants';
import { ClientsTableProps } from './interfaces';
import { TableComponent } from './Table';
import { Top } from './Top';

export const ClientsTable: FC<ClientsTableProps> = ({
  OpenTabRadio,
  setCountClients,
}) => {
  const {
    data: { permissions },
  } = useContext(AuthContext);

  const [selectedClientsUUIDs, setSelectedClientsUUIDs] = useState<string[]>(
    []
  );

  const { data: userGroupsData } = useQuery<TGroupsTitlesResponse>(
    GROUPS_TITLES,
    {
      fetchPolicy: 'cache-and-network',
      skip: !permissions?.has('client_browse'),
    }
  );

  const groups = userGroupsData?.userGroups;

  const [getClients, { data, loading, variables }] = useLazyQuery<
    IClientsResponse,
    IClientsRequest
  >(CLIENTS, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: VARIABLES,
    onCompleted: () => {
      setSelectedClientsUUIDs([]);
    },
  });

  useEffect(() => {
    getClients();
  }, [useReactiveVar(clientsReloadTrigger)]);

  useEffect(() => {
    if (!data) return;

    setCountClients(data.clients.paginatorInfo.total);
  }, [data]);

  const getDebouncedClients = useCallback(debounce(getClients, 400), [
    getClients,
  ]);

  return (
    <>
      <Top
        OpenTabRadio={OpenTabRadio}
        groups={groups}
        getDebouncedClients={getDebouncedClients}
        variables={variables}
        selectedClientsUUIDs={selectedClientsUUIDs}
        setSelectedClientsUUIDs={setSelectedClientsUUIDs}
        data={data}
        getClients={getClients}
      />
      <TableComponent
        groups={groups}
        getClients={getClients}
        data={data}
        loading={loading}
        variables={variables}
        selectedClientsUUIDs={selectedClientsUUIDs}
        setSelectedClientsUUIDs={setSelectedClientsUUIDs}
      />
    </>
  );
};
